/**
 * Mock data for the Bulk Update Jobs tab.
 *
 * A bulk job is created whenever a merchant runs a bulk reschedule or a bulk
 * status change from the subscription list. The job runs in the background and
 * every subscription in it succeeds or fails on its own, so a job can finish
 * partly done — which is the case the UI has to make obvious.
 */
export type BulkJobAction = 'reschedule' | 'status';
export type BulkJobStatus = 'running' | 'completed' | 'completedWithFailures' | 'failed';
export type BulkJobItemStatus = 'pending' | 'completed' | 'failed';
export interface BulkJobItem {
  subscriptionId: string;
  customerName: string;
  status: BulkJobItemStatus;
  /** The resulting value, e.g. the new charge date or new status. Empty when failed. */
  result?: string;
  /** Plain-English reason the subscription could not be updated. */
  error?: string;
  processedAt?: string;
}
export interface BulkJob {
  id: string;
  action: BulkJobAction;
  status: BulkJobStatus;
  affected: number;
  completed: number;
  failed: number;
  /** The staff member who ran the job — never the raw auth id. */
  actorName: string;
  actorEmail: string;
  createdAt: string;
  finishedAt?: string;
  /** The change being applied, shown on the detail summary. */
  newBillingDate?: string;
  newStatus?: string;
  notify: boolean;
  notifyMessage?: string;
  items: BulkJobItem[];
}
export const ACTION_LABELS: Record<BulkJobAction, string> = {
  reschedule: 'Reschedule',
  status: 'Status update'
};
export const JOB_STATUS_LABELS: Record<BulkJobStatus, string> = {
  running: 'Running',
  completed: 'Completed',
  completedWithFailures: 'Completed with failures',
  failed: 'Failed'
};
export const mockBulkJobs: BulkJob[] = [{
  id: 'b9acf1ab',
  action: 'reschedule',
  status: 'failed',
  affected: 2,
  completed: 0,
  failed: 2,
  actorName: 'Chris Mulhallen',
  actorEmail: 'chris@winepik.io',
  createdAt: '05 Aug 2026, 4:22 PM',
  finishedAt: '05 Aug 2026, 4:22 PM',
  newBillingDate: '26 Aug 2026',
  notify: true,
  notifyMessage: 'Harvest has run late this year, so we have moved your next delivery back a few weeks.',
  items: [{
    subscriptionId: 'SUB-29628203054',
    customerName: 'Marion Hagen',
    status: 'failed',
    error: "The billing cycle covering 2026-08-26 (cycle 2) has already been skipped — it can't also be rescheduled. Un-skip that cycle first, or choose a date that falls in a non-skipped cycle.",
    processedAt: '05 Aug 2026, 4:22 PM'
  }, {
    subscriptionId: 'SUB-29628235822',
    customerName: 'Gina Ziegler',
    status: 'failed',
    error: "The billing cycle covering 2026-08-26 (cycle 2) has already been skipped — it can't also be rescheduled. Un-skip that cycle first, or choose a date that falls in a non-skipped cycle.",
    processedAt: '05 Aug 2026, 4:22 PM'
  }]
}, {
  id: '4c1de907',
  action: 'reschedule',
  status: 'completedWithFailures',
  affected: 5,
  completed: 4,
  failed: 1,
  actorName: 'Jude Ellis',
  actorEmail: 'jude@winepik.io',
  createdAt: '04 Aug 2026, 11:05 AM',
  finishedAt: '04 Aug 2026, 11:06 AM',
  newBillingDate: '23 Sep 2026',
  notify: false,
  items: [{
    subscriptionId: 'SUB-29628170286',
    customerName: 'Kevin Kramer',
    status: 'completed',
    result: '23 Sep 2026',
    processedAt: '04 Aug 2026, 11:05 AM'
  }, {
    subscriptionId: 'SUB-29628137518',
    customerName: 'Lois Rhea',
    status: 'completed',
    result: '23 Sep 2026',
    processedAt: '04 Aug 2026, 11:05 AM'
  }, {
    subscriptionId: 'SUB-29628112904',
    customerName: 'Tom Whitfield',
    status: 'completed',
    result: '23 Sep 2026',
    processedAt: '04 Aug 2026, 11:06 AM'
  }, {
    subscriptionId: 'SUB-29628098221',
    customerName: 'Priya Raman',
    status: 'completed',
    result: '23 Sep 2026',
    processedAt: '04 Aug 2026, 11:06 AM'
  }, {
    subscriptionId: 'SUB-29628077310',
    customerName: 'Dan Corcoran',
    status: 'failed',
    error: 'This subscription was cancelled before the job ran, so its charge date cannot be changed.',
    processedAt: '04 Aug 2026, 11:06 AM'
  }]
}, {
  id: '7245873e',
  action: 'status',
  status: 'completed',
  affected: 3,
  completed: 3,
  failed: 0,
  actorName: 'Chris Mulhallen',
  actorEmail: 'chris@winepik.io',
  createdAt: '31 Jul 2026, 9:14 AM',
  finishedAt: '31 Jul 2026, 9:14 AM',
  newStatus: 'Paused',
  notify: true,
  notifyMessage: 'We are pausing club shipments over the shutdown period and will resume in the new year.',
  items: [{
    subscriptionId: 'SUB-29628044190',
    customerName: 'Alice Nguyen',
    status: 'completed',
    result: 'Paused',
    processedAt: '31 Jul 2026, 9:14 AM'
  }, {
    subscriptionId: 'SUB-29628031877',
    customerName: 'Robert Blaine',
    status: 'completed',
    result: 'Paused',
    processedAt: '31 Jul 2026, 9:14 AM'
  }, {
    subscriptionId: 'SUB-29628019043',
    customerName: 'Hannah Doyle',
    status: 'completed',
    result: 'Paused',
    processedAt: '31 Jul 2026, 9:14 AM'
  }]
}, {
  id: '7fef9ebc',
  action: 'reschedule',
  status: 'completed',
  affected: 4,
  completed: 4,
  failed: 0,
  actorName: 'Chris Mulhallen',
  actorEmail: 'chris@winepik.io',
  createdAt: '28 Jul 2026, 2:48 PM',
  finishedAt: '28 Jul 2026, 2:49 PM',
  newBillingDate: '15 Aug 2026',
  notify: false,
  items: [{
    subscriptionId: 'SUB-29627998210',
    customerName: 'Marco Silvestri',
    status: 'completed',
    result: '15 Aug 2026',
    processedAt: '28 Jul 2026, 2:48 PM'
  }, {
    subscriptionId: 'SUB-29627981446',
    customerName: 'Elise Fontaine',
    status: 'completed',
    result: '15 Aug 2026',
    processedAt: '28 Jul 2026, 2:48 PM'
  }, {
    subscriptionId: 'SUB-29627966032',
    customerName: 'Grant Hollis',
    status: 'completed',
    result: '15 Aug 2026',
    processedAt: '28 Jul 2026, 2:49 PM'
  }, {
    subscriptionId: 'SUB-29627950118',
    customerName: 'Yuki Tanaka',
    status: 'completed',
    result: '15 Aug 2026',
    processedAt: '28 Jul 2026, 2:49 PM'
  }]
}];